import i18nPlugin, {
  i18n,
  LANGUAGE_SETTING_KEY,
  persistLocalePreference,
  resolveLocale,
} from '~/i18n';
import { siteLocaleFromPath } from '~/lib/site-locale';

function readPathLang(): string | null {
  try {
    const pathname = import.meta.server ? useRequestURL().pathname : window.location.pathname;
    return siteLocaleFromPath(pathname);
  } catch {
    return null;
  }
}

function readUrlLangParam(): string | null {
  if (import.meta.server) {
    try {
      const url = useRequestURL();
      return url.searchParams.get('lang');
    } catch {
      return null;
    }
  }
  return new URLSearchParams(window.location.search).get('lang');
}

function readStorageLang(): string | null {
  if (!import.meta.client || typeof localStorage === 'undefined') return null;
  return localStorage.getItem(LANGUAGE_SETTING_KEY);
}

/**
 * 在 SSR / CSR 首屏渲染前解析并应用 locale。
 *
 * 根因修复：旧实现只在客户端读 localStorage，SSR 永远渲染 en，
 * hydration 后再切 ja → 刷新时先看到英文再闪回日文。
 * Cookie 可被 SSR 读取，与客户端共用 resolveLocale() 决策链。
 */
export default defineNuxtPlugin({
  name: 'i18n-locale',
  enforce: 'pre',
  setup(nuxtApp) {
    const langCookie = useCookie<string | null>(LANGUAGE_SETTING_KEY, {
      maxAge: 60 * 60 * 24 * 365,
      path: '/',
      sameSite: 'lax',
    });

    const resolved = resolveLocale({
      pathLang: readPathLang(),
      urlLang: readUrlLangParam(),
      cookieLang: langCookie.value,
      storageLang: readStorageLang(),
    });

    i18n.options.locale = resolved;

    // 写回 cookie：SSR 通过 Set-Cookie 下发；客户端同步 document.cookie
    if (langCookie.value !== resolved) {
      langCookie.value = resolved;
    }

    // 客户端把 localStorage 与 cookie 对齐（迁移仅 localStorage 的旧用户）
    if (import.meta.client) {
      persistLocalePreference(resolved);
    }

    nuxtApp.vueApp.use(i18nPlugin);

    nuxtApp.vueApp.config.globalProperties.$t = (
      key: string,
      params?: Record<string, string> | string,
      lang?: Record<string, Record<string, unknown>>
    ): string => {
      if (typeof params === 'object') {
        return i18n.get(key, params, lang);
      } else if (typeof params === 'string') {
        return i18n.get(key, '', lang) || params;
      }
      return i18n.get(key, params, lang);
    };

    return {
      provide: {
        i18n,
        t: (key: string, params?: Record<string, string> | string) => {
          if (typeof params === 'object') {
            return i18n.get(key, params);
          } else if (typeof params === 'string') {
            return i18n.get(key, '') || params;
          }
          return i18n.get(key, params);
        },
      },
    };
  },
});
